import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Link as LinkIcon, Undo, Redo } from "lucide-react";

const Btn = ({ active, onClick, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={`p-2 rounded transition-colors ${active ? "bg-navy text-white" : "text-navy/70 hover:bg-offwhite"}`}
  >
    {children}
  </button>
);

const TipTapEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line
  }, [editor]);

  if (!editor) return null;

  const setLink = () => {
    const url = window.prompt("Enter URL");
    if (url === null) return;
    if (url === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="border border-border rounded-lg bg-white overflow-hidden" data-testid="tiptap-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1.5 bg-offwhite/60">
        <Btn label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></Btn>
        <Btn label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></Btn>
        <Btn label="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></Btn>
        <Btn label="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={16} /></Btn>
        <Btn label="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></Btn>
        <Btn label="Ordered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></Btn>
        <Btn label="Link" active={editor.isActive("link")} onClick={setLink}><LinkIcon size={16} /></Btn>
        <div className="ml-auto flex">
          <Btn label="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo size={16} /></Btn>
          <Btn label="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo size={16} /></Btn>
        </div>
      </div>
      <EditorContent editor={editor} className="px-4 py-3 prose-wedding text-navy/85" />
    </div>
  );
};

export default TipTapEditor;
